import { IconButton, Tooltip, CircularProgress } from '@mui/material'
import GetAppOutlinedIcon from '@mui/icons-material/GetAppOutlined'
import axios from 'axios'
import { useAuthContext } from '../../hooks/useAuthContext'
import { getFormData } from '../../utility/dataEntry/formData'
import {
  FIELD_TYPES,
  PROCESS_ACTIVITY_STATUSES,
  EXCEL_COLUMNS,
  DROPDOWN_DEFAULT_VALUE,
  CURRENCY_COUNTRY_CODE,
  FIELD_CONSTRAINTS,
  ACTIVITY_TYPES,
} from '../../utility/constants'
import { useState, useEffect } from 'react'
import { getDataByProcessTemplateId, getValueForPlaceholder } from '../../utility/utilityFunctions'
import moment from 'moment'
import { downloadExcel } from '../../utility/excelDownloadUtils'
import { fieldTemplateService } from '../../services/fieldTemplateService'
import { userConfig } from '../../services/userConfigServices'
import { getFieldId } from '../../pages/DataEntryPage/utils/utils'
const COLOR_REVIEW_STATUS_ACCEPTED = 'FF038709' // #038709
const COLOR_REVIEW_STATUS_REJECTED = 'FFFF0000' // #ff0000

const isConditionallyEnabled = (enableOn, operation, value) => {
  switch (operation) {
    case FIELD_CONSTRAINTS.EQUAL:
    case FIELD_CONSTRAINTS.EQ: // this needs to be deprecate
      return enableOn == value // we only checking value irrespective of type
    case FIELD_CONSTRAINTS.GT:
      return value > enableOn
    case FIELD_CONSTRAINTS.GTE:
      return value >= enableOn
    case FIELD_CONSTRAINTS.LT:
      return value < enableOn
    case FIELD_CONSTRAINTS.LTE:
      return value <= enableOn
    default:
      return false
  }
}

const ExportExcelData = ({ processId }) => {
  const { user } = useAuthContext()
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
  const [loading, setLoading] = useState(false)

  const [usersList, setUsersList] = useState([])

  useEffect(() => {
    async function fetchUsersList() {
      try {
        const res = await userConfig.getDynamicList(user)
        setUsersList(res?.data?.usersList || [])
      } catch (err) {
        // ignore
      }
    }
    if (user) {
      fetchUsersList()
    }
  }, [user])

  let userId = user.id
  let axiosOptions = {}
  if (user) {
    axiosOptions = {
      headers: { Authorization: `Bearer ${user.token}` },
    }
  }

  async function getDataForExcel() {
    const processData = (
      await axios.get(`${NODE_BASE_URL}/data-entry/process-data/${processId}`, axiosOptions)
    ).data

    const activityTemplateId = processData[0].activity_template_id
    const processTemplateId = processData[0].process_template_id
    const selectedTableData = processData[0].selectable_tables_data

    const fieldTemplates = (
      await axios.get(`${NODE_BASE_URL}/template/field/process/${processTemplateId}`, axiosOptions)
    ).data

    const activityTemplates = (
      await axios.get(`${NODE_BASE_URL}/template/activity/${activityTemplateId}`, axiosOptions)
    ).data

    const activityTemplateName = activityTemplates?.activity_template_name

    const processTemplateData = (
      await axios.get(
        `${NODE_BASE_URL}/template/process/activity/${activityTemplateId}`,
        axiosOptions
      )
    ).data

    const prefix = processTemplateData[0].prefix

    //TODO: can't hardcode 10 here
    const listData = (await axios.get(`${NODE_BASE_URL}/template/list-data/company`, axiosOptions))
      .data

    const tableSequenceAndHeaders = await fieldTemplateService.getTableSequenceAndHeaders(
      user,
      processData[0].id
    )

    const data = processData[0].data
    const status = processData[0].status
    const version = processData[0].version
    const reviewData = processData[0].review_data
    const reviewCommentData = processData[0].review_comment_data
    const processReferenceNum = processData[0].process_reference_num

    return {
      activityTemplateName,
      activityTemplateId,
      prefix,
      version,
      status,
      processReferenceNum,
      fieldTemplates,
      listData,
      data,
      reviewData,
      reviewCommentData,
      processTemplateId,
      selectedTableData,
      tableSequenceAndHeaders,
      activityType: activityTemplates?.activity_type,
    }
  }

  const isFieldVisible = (field, data, user) => {
    let visibility = true

    if (field?.is_hidden) {
      return false
    }

    // conditional enable disable
    if (field?.source_field_template_id && field?.enable_on_value) {
      const sourceFieldValue = data[getFieldId(field?.source_field_template_id)]

      const isConditionallyVisible = isConditionallyEnabled(
        field.enable_on_value,
        field.operation,
        sourceFieldValue
      )

      if (!isConditionallyVisible) {
        return false
      }
    }

    // visible team types
    let isVisibleByType = null
    const visibleTeamTypes = field.visible_team_type?.filter((it) => it)
    if (visibleTeamTypes?.length > 0) {
      isVisibleByType = visibleTeamTypes?.some((vt) => user.team_types?.includes(vt))
    }

    // visible teams
    let isVisibleByTeam = true
    const visibleTeams = field.visible_team_id?.filter((it) => it)
    if (visibleTeams?.length > 0) {
      isVisibleByTeam = visibleTeams.some((vt) => user.teams?.includes(vt))
    }

    if (isVisibleByTeam != null && isVisibleByType != null) {
      return isVisibleByTeam || isVisibleByType
    } else if (isVisibleByTeam != null && isVisibleByType == null) {
      return isVisibleByTeam
    } else if (isVisibleByTeam == null && isVisibleByType != null) {
      return isVisibleByType
    }

    return visibility
  }

  // ─── Helper: resolve a subfield's display value ───────────────────────────
  const resolveSubfieldValue = async (subField, data, rows, rowIndex, listData, usersList) => {
    const key = 'f_' + subField.id.split('__')[0] + '__' + rows[rowIndex].row

    if (subField.data_structure === FIELD_TYPES.LIST) {
      const listDataId = data[key]
      for (let listDatum of listData) {
        if (listDatum.id === listDataId) {
          const alteredListData = await getValueForPlaceholder(key, listDatum.value, processId)
          return { v: alteredListData }
        }
      }
      return { v: '' }
    }

    if (subField.data_structure === FIELD_TYPES.LIST_MULTI) {
      const val = data[key]
      if (val === DROPDOWN_DEFAULT_VALUE) return { v: '' }
      const values = val.map((id) => listData.find((it) => it.id === id)?.value || '')
      return { v: values.join(', ') }
    }

    if (subField.data_structure === FIELD_TYPES.CURRENCY) {
      const val = data[key]
      const currencySymbol = CURRENCY_COUNTRY_CODE[subField.currency_code] || '₹'
      return { v: parseFloat(val), numFmt: `"${currencySymbol}" #,##0.00` }
    }

    if (subField.data_structure === FIELD_TYPES.DATE) {
      const val = data[key]
      return { v: moment(new Date(val)).format('DD MMM YYYY') }
    }

    if (subField.data_structure === FIELD_TYPES.TIMESTAMPED_BIGTEXT) {
      const val = data[key]
      if (val && Array.isArray(val)) {
        const formattedValue = val
          .map((item) => {
            const username = item.user_id
              ? usersList
                  ?.find((u) => u.id === item.user_id)
                  ?.value?.replace(/\s*\([^)]*\)\s*/g, '')
                  .trim()
              : null
            return `${item.timestamp} : ${item.text}${username ? ` - ${username}` : ''}`
          })
          .join('\n\n')
        return { v: formattedValue }
      }
      return { v: '' }
    }

    return { v: data[key] }
  }

  // ─── Helper: build a data row object for a table ──────────────────────────
  // -> uses visibleColIndex (increments only for visible subfields)
  //      instead of raw j (which would include hidden subfields, causing column mismatch)
  const buildTableDataRow = async (
    subFields,
    rows,
    rowIndex,
    data,
    listData,
    usersList,
    reviewData,
    activityType,
    tableFieldName
  ) => {
    let dataObj = {}
    let visibleColIndex = 0 // ← tracks Excel column position for visible fields only

    for (let j = 0; j < subFields.length; j++) {
      const subField = subFields[j]
      const isVisible = isFieldVisible(subField, data, user)

      console.debug(
        `[ExcelExport]   subField[${j}] "${subField.field_template_name}" | visible: ${isVisible} | raw j: ${j} | visibleColIndex: ${visibleColIndex} | will assign to col: ${isVisible ? EXCEL_COLUMNS[visibleColIndex] : 'SKIPPED'}`
      )

      if (!isVisible) {
        // ← j increments but visibleColIndex does NOT 
        continue
      }

      const vObj = await resolveSubfieldValue(subField, data, rows, rowIndex, listData, usersList)
      const reviewKey = 'r_' + subField.id.split('__')[0] + '__' + rows[rowIndex].row
      const color =
        !subField.is_read_only &&
        [ACTIVITY_TYPES.REVIEW, ACTIVITY_TYPES.LONG_RUNNING].includes(activityType)
          ? {
              argb: reviewData[reviewKey]
                ? COLOR_REVIEW_STATUS_ACCEPTED
                : COLOR_REVIEW_STATUS_REJECTED,
            }
          : null

      const actualValue = { ...vObj, s: { color } }

      dataObj = {
        ...dataObj,
        [EXCEL_COLUMNS[visibleColIndex]]: actualValue, // ← FIX: was EXCEL_COLUMNS[j]
      }

      visibleColIndex++
    }

    return dataObj
  }

  const handleExport = async () => {
    setLoading(true)

    const {
      activityTemplateName,
      activityTemplateId,
      prefix,
      version,
      status,
      processReferenceNum,
      fieldTemplates,
      listData,
      data,
      reviewData,
      processTemplateId,
      selectedTableData,
      tableSequenceAndHeaders,
      activityType,
    } = await getDataForExcel()

    let finalDataForExcel = []

    // for regular fields:
    let preData = []

    await getDataByProcessTemplateId(processTemplateId, axiosOptions)

    if (status === PROCESS_ACTIVITY_STATUSES.ACTIVE) {
      preData.push({ A: { v: `${activityTemplateName} (preview)` } })
    } else {
      preData.push({ A: { v: `${activityTemplateName}` } })
    }

    preData.push({ A: { v: ' ' } })
    preData.push({
      A: { v: 'Process Reference' },
      B: { v: `${prefix}-${processReferenceNum}` },
    })
    preData.push({
      A: { v: 'Version' },
      B: { v: version },
    })
    preData.push({ A: { v: ' ' } })

    let generalFormPlaceHolderValues = {}

    Object.keys(data).forEach((key) => {
      if (!key.includes('__')) {
        let fieldId = key.split('_')[1]
        fieldTemplates
          .filter((it) => isFieldVisible(it, data, user))
          .forEach((field) => {
            if (fieldId == field.id) {
              if (field.data_structure === FIELD_TYPES.LIST) {
                if (data[key] === DROPDOWN_DEFAULT_VALUE) {
                  generalFormPlaceHolderValues[
                    `[[${fieldId}]]`
                  ] = `[[${field.field_template_name}]]`
                } else {
                  listData.forEach((listDatum) => {
                    if (listDatum.id === data[key]) {
                      generalFormPlaceHolderValues[`[[${fieldId}]]`] = listDatum.value
                    }
                  })
                }
              } else if (field.data_structure === FIELD_TYPES.LIST_MULTI) {
                if (data[key] === DROPDOWN_DEFAULT_VALUE) {
                  generalFormPlaceHolderValues[
                    `[[${fieldId}]]`
                  ] = `[[${field.field_template_name}]]`
                } else {
                  let values = data[key].map((id) => {
                    return listData.find((it) => it.id === id)?.value || ''
                  })
                  generalFormPlaceHolderValues[`[[${fieldId}]]`] = values?.join(', ')
                }
              }
            }
          })
      }
    })
    Object.keys(data).forEach((key) => {
      if (!key.includes('__')) {
        let fieldId = key.split('_')[1]
        fieldTemplates
          .filter(
            (it) =>
              isFieldVisible(it, data, user) &&
              it.data_structure != FIELD_TYPES.TABLE &&
              !it.is_subfield
          )
          .forEach((field) => {
            if (fieldId == field.id) {
              if (field.data_structure === FIELD_TYPES.LIST) {
                listData.forEach((listDatum) => {
                  if (listDatum.id === data[key]) {
                    let value = listDatum.value
                    let pattern = /\[\[\d+\]\]/g
                    let alteredListData = value.replace(
                      pattern,
                      (m) => generalFormPlaceHolderValues[m]
                    )
                    preData.push({
                      A: { v: field.field_template_name },
                      B: {
                        v: alteredListData,
                        s: {
                          color:
                            !field.is_read_only &&
                            [ACTIVITY_TYPES.REVIEW, ACTIVITY_TYPES.LONG_RUNNING].includes(
                              activityType
                            )
                              ? {
                                  argb: reviewData[['r_' + field.id]]
                                    ? COLOR_REVIEW_STATUS_ACCEPTED
                                    : COLOR_REVIEW_STATUS_REJECTED,
                                }
                              : null,
                        },
                      },
                    })
                  }
                })
              } else if (field.data_structure === FIELD_TYPES.LIST_MULTI) {
                const val = data[key]

                if (val === DROPDOWN_DEFAULT_VALUE) {
                  preData.push({
                    A: { v: field.field_template_name },
                    B: { v: '' },
                  })
                } else {
                  let values = val.map((id) => {
                    return listData.find((it) => it.id === id)?.value || ''
                  })
                  preData.push({
                    A: { v: field.field_template_name },
                    B: {
                      v: values?.join(', '),
                      s: {
                        color:
                          !field.is_read_only &&
                          [ACTIVITY_TYPES.REVIEW, ACTIVITY_TYPES.LONG_RUNNING].includes(
                            activityType
                          )
                            ? {
                                argb: reviewData[['r_' + field.id]]
                                  ? COLOR_REVIEW_STATUS_ACCEPTED
                                  : COLOR_REVIEW_STATUS_REJECTED,
                              }
                            : null,
                      },
                    },
                  })
                }
              } else if (field.data_structure === FIELD_TYPES.TIMESTAMPED_BIGTEXT) {
                let value = data[key]

                if (value && Array.isArray(value)) {
                  const formattedValue = value.map((val) => {
                    const username = val.user_id
                      ? usersList
                          ?.find((u) => u.id === val.user_id)
                          ?.value?.replace(/\s*\([^)]*\)\s*/g, '')
                          .trim()
                      : null
                    return `${val.timestamp} : ${val.text}${username ? ` - ${username}` : ''}`
                  })
                  preData.push({
                    A: { v: `${field.field_template_name}:` },
                    B: {
                      v: formattedValue.join('\n\n'),
                      s: {
                        color:
                          !field.is_read_only &&
                          [ACTIVITY_TYPES.REVIEW, ACTIVITY_TYPES.LONG_RUNNING].includes(
                            activityType
                          )
                            ? {
                                argb: reviewData[['r_' + field.id]]
                                  ? COLOR_REVIEW_STATUS_ACCEPTED
                                  : COLOR_REVIEW_STATUS_REJECTED,
                              }
                            : null,
                      },
                    },
                  })
                }
              } else if (field.data_structure === FIELD_TYPES.DATE) {
                let value = data[key]
                preData.push({
                  A: { v: field.field_template_name },
                  B: {
                    v: moment(new Date(value)).format('DD MMM YYYY'),
                    s: {
                      color:
                        !field.is_read_only &&
                        [ACTIVITY_TYPES.REVIEW, ACTIVITY_TYPES.LONG_RUNNING].includes(activityType)
                          ? {
                              argb: reviewData[['r_' + field.id]]
                                ? COLOR_REVIEW_STATUS_ACCEPTED
                                : COLOR_REVIEW_STATUS_REJECTED,
                            }
                          : null,
                    },
                  },
                })
              } else if (field.data_structure === FIELD_TYPES.CURRENCY) {
                let value = data[key]
                const currencySymbol = CURRENCY_COUNTRY_CODE[field.currency_code] || '₹'
                const numFmt = `"${currencySymbol}" #,##0.00`
                preData.push({
                  A: { v: field.field_template_name },
                  B: {
                    v: parseFloat(value),
                    numFmt: numFmt,
                    s: {
                      color:
                        !field.is_read_only &&
                        [ACTIVITY_TYPES.REVIEW, ACTIVITY_TYPES.LONG_RUNNING].includes(activityType)
                          ? {
                              argb: reviewData[['r_' + field.id]]
                                ? COLOR_REVIEW_STATUS_ACCEPTED
                                : COLOR_REVIEW_STATUS_REJECTED,
                            }
                          : null,
                    },
                  },
                })
              } else {
                preData.push({
                  A: { v: field.field_template_name },
                  B: {
                    v: data[key],
                    s: {
                      color:
                        !field.is_read_only &&
                        [ACTIVITY_TYPES.REVIEW, ACTIVITY_TYPES.LONG_RUNNING].includes(activityType)
                          ? {
                              argb: reviewData[['r_' + field.id]]
                                ? COLOR_REVIEW_STATUS_ACCEPTED
                                : COLOR_REVIEW_STATUS_REJECTED,
                            }
                          : null,
                    },
                  },
                })
              }
            }
          })
      }
    })

    finalDataForExcel = [
      ...finalDataForExcel,
      {
        name: 'General Data',
        data: preData.map((it) => ({ ...it, A: { ...it.A, s: { bold: true } } })),
        columnsWidth: { A: 30, B: 30 },
      },
    ]

    // for sub fields
    const { formData } = await getFormData(
      10, // TODO: hardcoding 10
      activityTemplateId,
      processId,
      userId,
      axiosOptions
    )

    preData = []

    for (let field of formData) {
      let excelData = []

      if (!isFieldVisible(field, data, user)) {
        continue
      }

      if (field.data_structure === FIELD_TYPES.TABLE) {
        const rows =
          tableSequenceAndHeaders.find((it) => it.table_field_template_id == field.id)
            ?.sequence_header_map || []
        // ── Build header row ──────────────────────────────────────────────
        // Uses its own counter i (visible-only), same logic as data rows below
        let headingObj = {}
        let i = 0
        field.subFields[0]?.forEach((subField, rawIndex) => {
          const isVisible = isFieldVisible(subField, data, user)
          if (isVisible) {
            headingObj = {
              ...headingObj,
              [EXCEL_COLUMNS[i]]: { v: subField.field_template_name, s: { bold: true } },
            }
            i++
          }
        })

        preData.push(headingObj)
        excelData.push(headingObj)

        // ── Build data rows ───────────────────────────────────────────────
        for (let i = 0; i < rows.length; i++) {
          let dataObj = {}
          let subFields = field.subFields[0]

          if (subFields?.length > 0) {
            if (field.is_selectable_table == true) {
              for (const [key, value] of Object.entries(selectedTableData)) {
                if (key.split('_')[1] == field.id) {
                  if (rows[i].is_header) {
                    dataObj = {
                      ...dataObj,
                      [EXCEL_COLUMNS[0]]: { v: rows[i].header_value, s: { bold: true } },
                    }
                  } else {
                    for (let val of value) {
                      if (val == rows[i].row) {
                        // ↓ FIX: pass subFields into helper which uses visibleColIndex internally
                        dataObj = await buildTableDataRow(
                          subFields,
                          rows,
                          i,
                          data,
                          listData,
                          usersList,
                          reviewData,
                          activityType,
                          field.field_template_name
                        )
                      }
                    }
                  }
                }
              }
            } else {
              if (rows[i].is_header) {
                dataObj = {
                  ...dataObj,
                  [EXCEL_COLUMNS[0]]: { v: rows[i].header_value, s: { bold: true } },
                }
              } else {
                // ↓ FIX: pass subFields into helper which uses visibleColIndex internally
                dataObj = await buildTableDataRow(
                  subFields,
                  rows,
                  i,
                  data,
                  listData,
                  usersList,
                  reviewData,
                  activityType,
                  field.field_template_name
                )
              }
            }
          }

          if (Object.keys(dataObj).length > 0) {
            excelData.push(dataObj)
          }
        }

        finalDataForExcel = [
          ...finalDataForExcel,
          {
            name: field.field_template_name,
            data: excelData,
          },
        ]
      }
    }

    downloadExcel(finalDataForExcel, `${prefix}-${processReferenceNum}`)

    setLoading(false)
  }

  return (
    <Tooltip title="Download data in Excel format">
      <IconButton onClick={handleExport} size="large">
        {loading ? <CircularProgress color="primary" size={'25px'} /> : <GetAppOutlinedIcon />}
      </IconButton>
    </Tooltip>
  )
}

export default ExportExcelData
